/**
 * 14 #7 — when the Action note and the status disagree, say so and offer the fix:
 *   "CODE C"                                   → Suggest reason: Consultant code C · Accept
 *   "Submitted via email – awaiting approval"  → Looks submitted: move to Submitted?
 *   "APPROVED" ("… with comments" / "as noted")  → Action says approved: close as Approved (w/ comments)?
 *   approved or completed drawings that still carry a blocker → clear the blocker (Review dialog only)
 * The rules (model.js mismatchOf) skip negated / future notes ("to be submitted", "not yet approved").
 * One click fixes one drawing; the Review dialog accepts whole groups at once (one bulk edit, one undo step).
 */
import { useMemo, useState } from 'react';
import { Loader2, TriangleAlert, WandSparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { notify } from '@/components/ui/notify';
import { formatNumber } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { BLOCKER_LABELS, MISMATCH_KINDS, drawingTitle, labelize, mismatchOf } from './model';
import { SdCheckbox } from './SdUi';

const fmt = (value) => formatNumber(value, { maxDecimals: 0 });

/** The change a suggestion makes, as a /bulk-edit item. */
export const suggestionChange = (drawing, found = mismatchOf(drawing)) => (found ? { id: drawing.id, ...found.change, change_note: found.note } : null);

/** The tag under an Action note: one click applies the suggestion to this drawing. */
export function SuggestionTag({ drawing, onApply }) {
  const found = mismatchOf(drawing);
  const [busy, setBusy] = useState(false);
  if (!found || found.kind === 'stale') return null;
  const apply = async (event) => {
    event.stopPropagation();
    setBusy(true);
    try {
      await onApply([suggestionChange(drawing, found)], found);
    } catch (err) {
      notify.error('Could not apply the suggestion', { description: getErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };
  if (found.kind === 'code_c') {
    return (
      <span className="mt-0.5 flex min-w-0 items-center gap-1.5 whitespace-nowrap" data-testid={`suggestion-${drawing.id}`}>
        <span className="inline-flex h-[20px] min-w-0 items-center gap-1 rounded-[5px] bg-[color:var(--navy-50)] px-1.5 text-[11px] font-semibold text-[color:var(--navy-600)] dark:text-[color:var(--brand-ink)]" title="The action note says CODE C: set the reason to Consultant comments (code C)">
          <WandSparkles className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="truncate">{found.label}</span>
        </span>
        <button
          type="button"
          onClick={apply}
          onKeyDown={(event) => event.stopPropagation()}
          disabled={busy}
          className="shrink-0 rounded px-0.5 text-[12px] font-bold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          aria-label={`Accept: set the reason of ${drawingTitle(drawing)} to Consultant comments (code C)`}
          data-testid={`suggestion-accept-${drawing.id}`}
        >
          {busy ? <Loader2 className="inline h-3 w-3 animate-spin" aria-hidden="true" /> : 'Accept'}
        </button>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={apply}
      onKeyDown={(event) => event.stopPropagation()}
      disabled={busy}
      title={`${found.short}: ${drawingTitle(drawing)}`}
      className="mt-0.5 inline-flex h-[20px] max-w-full items-center gap-1 rounded-[5px] bg-status-amber-bg px-1.5 text-[11px] font-semibold text-status-amber-ink hover:ring-1 hover:ring-[color:rgb(217_119_6/0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
      data-testid={`suggestion-${drawing.id}`}
    >
      {busy ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" /> : <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />}
      <span className="truncate">{found.label}</span>
    </button>
  );
}

/**
 * The callout's "Review N mismatches": each kind as a group with its drawings, ticked by default; Accept applies
 * the ticked ones in one bulk edit.
 */
export function MismatchDialog({ open, onOpenChange, groups, onApply, onOpenDrawing }) {
  const [unticked, setUnticked] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [lastOpen, setLastOpen] = useState(false);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setUnticked(new Set());
      setExpanded(new Set());
      setError('');
    }
  }
  const kinds = MISMATCH_KINDS.filter((item) => (groups[item.kind] || []).length);
  const chosen = useMemo(() => kinds.flatMap((item) => groups[item.kind].filter((drawing) => !unticked.has(drawing.id))), [groups, kinds, unticked]);
  const toggle = (ids, on) => setUnticked((current) => {
    const next = new Set(current);
    ids.forEach((id) => (on ? next.delete(id) : next.add(id)));
    return next;
  });

  const apply = async () => {
    if (!chosen.length) return;
    setBusy(true);
    setError('');
    try {
      await onApply(chosen.map((drawing) => suggestionChange(drawing)).filter(Boolean));
      onOpenChange(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not apply the fixes'));
    } finally {
      setBusy(false);
    }
  };

  const unblocked = chosen.filter((drawing) => ['approved', 'submitted'].includes(mismatchOf(drawing)?.kind)).length;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden rounded-2xl border-line bg-surface p-0 text-ink" data-testid="mismatch-dialog">
        <div className="flex max-h-[90vh] min-w-0 flex-col">
          <DialogHeader className="border-b border-line-2 px-5 pb-3 pt-5 text-left">
            <DialogTitle className="font-heading text-[17px] font-bold text-ink">Action notes that disagree with the status</DialogTitle>
            <DialogDescription className="text-[13px] text-ink-3">
              Untick anything that is really blocked. Each drawing keeps the change in its history; the message afterwards has Undo.
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
            {kinds.map((item) => {
              const list = groups[item.kind];
              const ticked = list.filter((drawing) => !unticked.has(drawing.id)).length;
              const open_ = expanded.has(item.kind);
              const shown = open_ ? list : list.slice(0, 4);
              return (
                <section key={item.kind} className="rounded-xl border border-line" data-testid={`mismatch-group-${item.kind}`}>
                  <label className="flex cursor-pointer items-center gap-2.5 rounded-t-xl bg-surface-2 px-3 py-2.5">
                    <SdCheckbox
                      checked={ticked === list.length}
                      indeterminate={ticked > 0 && ticked < list.length}
                      onCheckedChange={(value) => toggle(list.map((drawing) => drawing.id), Boolean(value))}
                      aria-label={item.title}
                      data-testid={`mismatch-check-${item.kind}`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-bold text-ink">{item.title}</span>
                      <span className="block text-[12px] text-ink-3">→ {item.action}</span>
                    </span>
                    <span className="shrink-0 text-[12px] font-semibold tabular-nums text-ink-2">{ticked === list.length ? fmt(list.length) : `${fmt(ticked)} of ${fmt(list.length)}`}</span>
                  </label>
                  <ul className="divide-y divide-line-2">
                    {shown.map((drawing) => (
                      <li key={drawing.id} className="flex items-center gap-2.5 px-3 py-1.5 pl-9 text-[12.5px]">
                        <SdCheckbox checked={!unticked.has(drawing.id)} onCheckedChange={(value) => toggle([drawing.id], Boolean(value))} aria-label={`Fix ${drawingTitle(drawing)}`} />
                        <button type="button" className="min-w-0 flex-1 truncate text-left font-semibold text-ink hover:underline" onClick={() => { onOpenChange(false); onOpenDrawing(drawing); }}>
                          {drawingTitle(drawing)}
                        </button>
                        <span className="hidden min-w-0 max-w-[40%] truncate text-ink-3 sm:inline">{drawing.project}</span>
                        {item.kind === 'stale' ? (
                          <span className="max-w-[30%] shrink-0 truncate text-[11.5px] text-ink-3" title={`Blocker: ${labelize(drawing.blocker, BLOCKER_LABELS)}`}>{labelize(drawing.blocker, BLOCKER_LABELS)}</span>
                        ) : (
                          <span className="max-w-[30%] shrink-0 truncate font-mono text-[11px] text-ink-3" title={drawing.action}>{drawing.action}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  {list.length > shown.length ? (
                    <button type="button" className="w-full border-t border-line-2 px-3 py-1.5 pl-9 text-left text-[12.5px] font-semibold text-brand-ink hover:bg-surface-hover" onClick={() => setExpanded((current) => new Set(current).add(item.kind))}>
                      + {fmt(list.length - shown.length)} more
                    </button>
                  ) : null}
                </section>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line-2 px-5 py-3">
            <p className={cn('text-[12.5px]', error ? 'font-semibold text-status-red-ink' : 'text-ink-3')} role={error ? 'alert' : undefined} data-testid="mismatch-summary">
              {error || (unblocked ? `${fmt(unblocked)} drawings leave the Blocked list` : `${fmt(chosen.length)} ticked`)}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button type="button" onClick={apply} disabled={!chosen.length || busy} data-testid="mismatch-apply">
                {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
                {busy ? 'Applying…' : `Accept ${fmt(chosen.length)}`}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
